/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Workers-safe robots.txt rule builder and serializer, kept in its own module for the same
 * reason as robots.constants.ts: it must be importable by the Cloudflare Workers runtime, which
 * cannot load robots.ts (that file imports @cosmos/common-utils and reads env vars).
 *
 * Keep it dependency-free apart from robots.constants.ts — in particular no `next` import, even
 * a type-only one, since the Workers packages do not have Next in their tsconfig.
 *
 * Two reasons this exists rather than leaving serialization entirely to Next.js:
 *
 * 1. `cloudflare-routers/epicgames` serves `/robots.txt` at the edge and cannot use Next's
 *    metadata route. It previously hand-built the file as a template string, importing only the
 *    constants while hard-coding the group STRUCTURE — so a new group added in robots.ts would
 *    silently never reach that domain. Pointing both at this function removes that drift.
 * 2. Nothing else can show a reviewer the literal file. For a change that is a legal rights
 *    reservation, "what will crawlers actually see" has to be reviewable in the diff, which is
 *    why robots.test.ts snapshots this output.
 */

import {
	aiSearchUserAgents,
	aiTrainingUserAgents,
	CONTENT_SIGNAL,
	CONTENT_SIGNAL_PREAMBLE,
	disallowedPaths,
	RESERVE_AI_TRAINING,
} from "./robots.constants";

/**
 * Structural subset of Next's `MetadataRoute.Robots` rule shape. Declared locally rather than
 * imported so this module stays loadable anywhere; it is assignable from the real Next type.
 */
export interface RenderableRobotsRule {
	userAgent?: string | string[];
	allow?: string | string[];
	disallow?: string | string[];
	crawlDelay?: number;
	/** Unrecognised records such as `Content-Signal`, passed through verbatim. */
	other?: Record<string, string | number | Array<string | number>>;
}

/**
 * A group as produced by {@link buildRobotsRules}, where `userAgent` is always set. Next's own
 * `RobotsRule` requires `userAgent`, so the builder has to promise it — declaring the return as
 * the looser {@link RenderableRobotsRule} makes the result unassignable to `MetadataRoute.Robots`
 * (caught by the declaration-emit build, not by `tsc --noEmit`).
 */
export type BuiltRobotsRule = RenderableRobotsRule & { userAgent: string | string[] };

/** Structural subset of Next's `MetadataRoute.Robots`. */
export interface RenderableRobots {
	rules?: RenderableRobotsRule | RenderableRobotsRule[];
	host?: string;
	sitemap?: string | string[];
}

/** Mirrors Next's internal `resolveArray`. */
const toArray = <T>(value: T | T[]): T[] => (Array.isArray(value) ? value : [value]);

/**
 * Serialize a robots config to the literal text Next.js emits, matching its field order:
 * per group, `User-Agent` lines, then `Allow`, `Disallow`, `Crawl-delay`, then any `other`
 * keys, then a blank line — followed by `Host` and `Sitemap` once at the end.
 *
 * That order is safe under RFC 9309: rule precedence is longest-match, so line order is
 * irrelevant to compliant parsers, and §2.2.4 requires unrecognised records to be ignored
 * without terminating the group.
 */
export function renderRobotsTxt(config: RenderableRobots): string {
	// Deliberately a line-for-line port of Next's `resolveRobots`, including its truthiness
	// checks and the trailing blank line after every group. Divergence here would make the
	// snapshot in robots.test.ts a lie about what the Next sites actually serve, so the
	// quirks are reproduced rather than tidied:
	//   - `rule.userAgent || ["*"]` — an EMPTY ARRAY is truthy, so it yields no `User-Agent`
	//     line at all, whereas `""`/`undefined` fall back to `*`.
	//   - `if (rule.allow)` / `if (rule.disallow)` — `""` is falsy and omitted entirely, so
	//     an empty-string disallow cannot be used as the idiomatic `Disallow:` marker.
	//   - `if (rule.crawlDelay)` — `0` is falsy and omitted.
	//   - `other` entries with a `null`/`undefined` value are skipped.
	//   - No trailing trim: with no `host` and no `sitemap` the output ends in a blank line.
	let content = "";

	for (const rule of toArray(config.rules ?? [])) {
		for (const agent of toArray(rule.userAgent || ["*"])) content += `User-Agent: ${agent}\n`;

		if (rule.allow) for (const item of toArray(rule.allow)) content += `Allow: ${item}\n`;
		if (rule.disallow) for (const item of toArray(rule.disallow)) content += `Disallow: ${item}\n`;
		if (rule.crawlDelay) content += `Crawl-delay: ${rule.crawlDelay}\n`;

		if (rule.other) {
			for (const [key, value] of Object.entries(rule.other)) {
				if (value === null || value === undefined) continue;
				for (const entry of toArray(value)) content += `${key}: ${entry}\n`;
			}
		}

		content += "\n";
	}

	if (config.host) content += `Host: ${config.host}\n`;
	if (config.sitemap) for (const item of toArray(config.sitemap)) content += `Sitemap: ${item}\n`;

	return content;
}

/** Options for {@link buildRobotsRules}. */
export interface RobotsRulesOptions {
	/**
	 * Reserve the site's content from AI training: emit a `Disallow: /` group for
	 * {@link aiTrainingUserAgents} and a `Content-Signal` declaration on the crawlable groups.
	 *
	 * Defaults to the shared {@link RESERVE_AI_TRAINING} switch — currently `false`, pending the
	 * legal sign-off MW-12938 gates on. Pass `true` explicitly to opt a single brand in ahead of
	 * the rest. A reservation asserted for a brand that has not signed off is worse than a missing
	 * one: withdrawing it once crawlers have observed it is messier than adding it later.
	 */
	reserveAiTraining?: boolean;
}

/**
 * Build the robots.txt rule groups — the single source of group STRUCTURE for every consumer,
 * Next sites and the epicgames edge worker alike. It lives in this Workers-safe module precisely
 * so the worker can call it instead of re-listing the groups; `robotsTxt.test.ts` asserts the
 * worker's output equals `renderRobotsTxt({ rules: buildRobotsRules(), … })`, which makes
 * structural drift impossible rather than merely documented.
 *
 * Every call returns fresh arrays and a fresh `other` object, so a consumer that mutates one
 * group (as cosmos-website does, appending per-tenant `robotsDisallow`) cannot leak into another
 * group or into a shared module-level singleton.
 *
 * At most three groups, relying only on behaviour identical across RFC 9309, Google and Bing —
 * exact-token group matching and `Disallow` prefix matching. Never on Allow-beating-Disallow
 * (Bing has never documented its conflict rule), on group ordering, or on a named group
 * inheriting anything from `*`.
 */
export function buildRobotsRules({
	reserveAiTraining = RESERVE_AI_TRAINING,
}: RobotsRulesOptions = {}): BuiltRobotsRule[] {
	// `other` carries the Content-Signal line: Next's serializer passes unknown keys through
	// verbatim (asserted against Next's real `resolveRobots` in robots.test.ts), and RFC 9309
	// §2.2.4 requires parsers to ignore unrecognised records without terminating the group.
	const contentSignal = (): Pick<RenderableRobotsRule, "other"> =>
		reserveAiTraining ? { other: { "Content-Signal": CONTENT_SIGNAL } } : {};

	const rules: BuiltRobotsRule[] = [
		{
			userAgent: "*",
			disallow: [...disallowedPaths],
			...contentSignal(),
		},
		{
			// Repeats `disallowedPaths` on purpose: a crawler obeys ONLY the group whose product
			// token matches its own and ignores `*` entirely (RFC 9309 §2.2.1), so without its own
			// copy this group would be a blanket allow over /account, /dashboard, /id and the
			// faceted-search param URLs.
			userAgent: [...aiSearchUserAgents],
			allow: "/",
			disallow: [...disallowedPaths],
			...contentSignal(),
		},
	];

	if (reserveAiTraining) {
		// A bare `Disallow: /` — no `allow`, and no copy of `disallowedPaths`. It already blocks a
		// strict superset of those paths, so a copy would be noise, and omitting `allow` means no
		// parser has to resolve any precedence to honour this group.
		rules.push({
			userAgent: [...aiTrainingUserAgents],
			disallow: "/",
		});
	}

	return rules;
}

/** Options for {@link buildRobotsTxt}. */
export interface RobotsTxtOptions extends RobotsRulesOptions {
	/** Absolute sitemap URL(s), emitted once after the groups. */
	sitemap?: string | string[];
	host?: string;
	/**
	 * Extra site-specific paths appended to every crawlable group — the multi-tenant case, where
	 * one app serves several brands with their own disallow lists.
	 *
	 * Appended to each group that lists paths, because a crawler obeys only the group matching its
	 * own token; adding them to just the `*` group would leave the named agents free to crawl them.
	 * Deliberately NOT appended to a blanket `Disallow: /` group, which already covers everything.
	 */
	extraDisallow?: readonly string[];
}

/**
 * Build a complete robots.txt document — preamble, groups, sitemaps — as text.
 *
 * This is the entry point for any consumer that serves robots.txt as a string: the epicgames
 * edge worker, and any Next site that swaps `app/robots.ts` for a `app/robots.txt/route.ts`
 * handler. Next's own metadata route CANNOT be used when the reservation is on, because
 * `resolveRobots` has no comment field and so cannot emit the preamble.
 *
 * The preamble is emitted if and only if the Content Signal is — they are one artifact. Shipping
 * `ai-train=no` without the definitions takes on the legal assertion while giving up most of its
 * evidentiary value, so the two cannot be enabled independently by accident.
 */
export function buildRobotsTxt({
	reserveAiTraining = RESERVE_AI_TRAINING,
	sitemap,
	host,
	extraDisallow = [],
}: RobotsTxtOptions = {}): string {
	const rules = buildRobotsRules({ reserveAiTraining }).map((rule) =>
		extraDisallow.length === 0 || rule.disallow === "/"
			? rule
			: {
					...rule,
					disallow: [
						...(Array.isArray(rule.disallow) ? rule.disallow : rule.disallow ? [rule.disallow] : []),
						...extraDisallow,
					],
				},
	);
	const body = renderRobotsTxt({ rules, sitemap, host });
	// Comments never terminate a group and are ignored by every parser (RFC 9309 §2.2.3/§2.2.4),
	// so a leading block is safe anywhere; the top is where a human reader expects it.
	return reserveAiTraining ? `${CONTENT_SIGNAL_PREAMBLE}\n${body}` : body;
}
